import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import JSZip from 'jszip';

export type ImageFormat = 'png' | 'jpg' | 'webp' | 'bmp' | 'gif' | 'svg' | 'pdf';

interface ConversionOptions {
  formats: ImageFormat[];
  quality?: number;
  compression?: number;
}

interface ConvertedImage {
  id: string;
  name: string;
  format: ImageFormat;
  blob: Blob;
  url: string;
  size: number;
}

async function convertToFormat(
  file: File,
  format: ImageFormat,
  quality: number = 0.92
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      ctx.drawImage(img, 0, 0);
      
      let mimeType = 'image/png';
      switch (format) {
        case 'jpg':
          mimeType = 'image/jpeg';
          break;
        case 'webp':
          mimeType = 'image/webp';
          break;
        case 'bmp':
          mimeType = 'image/bmp';
          break;
        case 'gif':
          mimeType = 'image/gif';
          break;
        case 'png':
        default:
          mimeType = 'image/png';
      }
      
      if (format === 'svg') {
        const svgData = generateSVGWithImage(canvas.toDataURL('image/png'), canvas.width, canvas.height);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml' });
        resolve(svgBlob);
      } else if (format === 'pdf') {
        const pdfData = generateSimplePDF(canvas.toDataURL('image/jpeg', 0.9), canvas.width, canvas.height);
        const pdfBlob = new Blob([pdfData], { type: 'application/pdf' });
        resolve(pdfBlob);
      } else {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create blob'));
          },
          mimeType,
          quality
        );
      }
      
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

function generateSVGWithImage(base64Image: string, width: number, height: number): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <image xlink:href="${base64Image}" width="${width}" height="${height}"/>
</svg>`;
}

function generateSimplePDF(base64Image: string, width: number, height: number): string {
  const pdfWidth = Math.min(width, 595);
  const pdfHeight = (height / width) * pdfWidth;
  
  return `%PDF-1.4
1 0 obj
<< /Type /Catalog /Pages 2 0 R >>
endobj
2 0 obj
<< /Type /Pages /Kids [3 0 R] /Count 1 >>
endobj
3 0 obj
<< /Type /Page /Parent 2 0 R /Resources 4 0 R /MediaBox [0 0 ${pdfWidth} ${pdfHeight}] /Contents 5 0 R >>
endobj
4 0 obj
<< /XObject << /Im1 6 0 R >> >>
endobj
5 0 obj
<< /Length 44 >>
stream
q
${pdfWidth} 0 0 ${pdfHeight} 0 0 cm
/Im1 Do
Q
endstream
endobj
6 0 obj
<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${base64Image.length} >>
stream
${base64Image}
endstream
endobj
xref
0 7
0000000000 65535 f
0000000009 00000 n
0000000058 00000 n
0000000115 00000 n
0000000230 00000 n
0000000279 00000 n
0000000373 00000 n
trailer
<< /Size 7 /Root 1 0 R >>
startxref
${600 + base64Image.length}
%%EOF`;
}

export function useFormatConversion() {
  const [isConverting, setIsConverting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [convertedImages, setConvertedImages] = useState<ConvertedImage[]>([]);

  const convertImage = useCallback(async (
    file: File,
    options: ConversionOptions
  ): Promise<ConvertedImage[]> => {
    const results: ConvertedImage[] = [];
    const baseName = file.name.replace(/\.[^/.]+$/, '');
    
    for (const format of options.formats) {
      try {
        const blob = await convertToFormat(file, format, options.quality);
        const converted: ConvertedImage = {
          id: crypto.randomUUID(),
          name: `${baseName}.${format}`,
          format,
          blob,
          url: URL.createObjectURL(blob),
          size: blob.size,
        };
        results.push(converted);
      } catch (error) {
        console.error(`Error converting to ${format}:`, error);
        toast.error(`Failed to convert to ${format.toUpperCase()}`);
      }
    }
    
    return results;
  }, []);

  const convertBatch = useCallback(async (
    files: File[],
    options: ConversionOptions
  ) => {
    setIsConverting(true);
    setProgress(0);
    setConvertedImages([]);
    
    const allResults: ConvertedImage[] = [];
    
    for (let i = 0; i < files.length; i++) {
      toast.loading(`Converting image ${i + 1} of ${files.length}...`, { id: 'conversion-progress' });
      
      const results = await convertImage(files[i], options);
      allResults.push(...results);
      setConvertedImages((prev) => [...prev, ...results]);
      
      setProgress(((i + 1) / files.length) * 100);
    }
    
    toast.success(`Successfully converted ${files.length} images to ${options.formats.length} format(s)`, { id: 'conversion-progress' });
    setIsConverting(false);
    
    return allResults;
  }, [convertImage]);

  const downloadAsZip = useCallback(async (images: ConvertedImage[], zipName: string = 'converted-images') => {
    const zip = new JSZip();
    
    for (const img of images) {
      zip.file(img.name, img.blob);
    }
    
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${zipName}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('ZIP file downloaded successfully');
  }, []);

  const clearResults = useCallback(() => {
    convertedImages.forEach(img => URL.revokeObjectURL(img.url));
    setConvertedImages([]);
    setProgress(0);
  }, [convertedImages]);

  return {
    isConverting,
    progress,
    convertedImages,
    convertImage,
    convertBatch,
    downloadAsZip,
    clearResults,
  };
}
